import { isArray, isString, isObject, isEmpty, isEqual, isFunction, debounce, merge as lodashMergeObj } from "lodash";

const withBasicQueriesError = message => {
	throw new Error("[WithBasicQueries]: " + message);
};

const syncQueryError = message => {
	throw new Error("[SyncQuery]: " + message);
};

const isValidQueryKey = (keyName, dataKeys) => dataKeys.includes(keyName);

const getDefaultSyncQueryConfig = field => ({
	dataName: field,
	queryName: field,
	dataToQuery: {
		formatter: value => value,
		immediate: false,
		deep: false,
		didUpdate: () => {},
		debounceTime: 0
	},
	queryToData: {
		formatter: value => value,
		immediate: true,
		deep: false // P.S. watching deep of a string makes no sense
	}
});

const handleSyncQuery = function({ dataName, queryName, dataToQuery, queryToData }) {
	(() => {
		if (typeof this.$router === "undefined") {
			syncQueryError("vue-router is required");
		}

		if (!isValidQueryKey(dataName, Object.keys(this._data))) {
			syncQueryError(`${dataName} is not declare in $data`);
		}

		// backup the default value
		const defaultDataValue = this[dataName];

		// sync data to query
		this.$watch(
			dataName,
			debounce(function(newValue, oldValue) {
				const route = { query: { ...this.$route.query } };

				if (newValue === null || newValue === undefined || (isObject(newValue) && isEmpty(newValue))) {
					if (this.$route.query.hasOwnProperty(queryName)) {
						delete route.query[queryName];
						this.$router.push(route);
					}
					return;
				}

				Object.defineProperty(route.query, queryName, {
					value: dataToQuery.formatter(newValue, oldValue),
					enumerable: true
				});

				let isValueChanged = queryToData.formatter(this.$route.query[queryName]) !== newValue;
				if (isObject(newValue)) {
					isValueChanged = !isEqual(queryToData.formatter(this.$route.query[queryName]), newValue);
				}

				if (!this.$route.query.hasOwnProperty(queryName) || (this.$route.query.hasOwnProperty(queryName) && isValueChanged)) {
					this.$router.push(route);
				}

				if (isFunction(dataToQuery.didUpdate)) {
					dataToQuery.didUpdate();
				}
			}, dataToQuery.debounceTime),
			dataToQuery
		);

		// sync query to data
		this.$watch(
			`$route.query.${queryName}`,
			function(newValue, oldValue) {
				this[dataName] = queryToData.formatter(newValue, oldValue) || defaultDataValue;
			},
			queryToData
		);
	})();
};

export const syncQuery = {
	methods: {
		syncQuery(queryConfigs) {
			if (isEmpty(queryConfigs)) {
				syncQueryError("Empty query config");
			}

			if (isString(queryConfigs)) {
				handleSyncQuery.call(this, getDefaultSyncQueryConfig(queryConfigs));
				return;
			}

			if (isObject(queryConfigs) && !isArray(queryConfigs)) {
				handleSyncQuery.call(this, lodashMergeObj(getDefaultSyncQueryConfig(queryConfigs.dataName), queryConfigs));
				return;
			}

			if (isArray(queryConfigs)) {
				queryConfigs.forEach(item => this.syncQuery(item));
				return;
			}

			syncQueryError("Invalid config type. Only support: String, Object, Array");
		}
	}
};

export const withBasicQueries = {
	mixins: [syncQuery],
	data() {
		return {
			detailId: null,
			searchText: "",
			currentPage: 1,
			orderBy: null
		};
	},
	created() {
		if (!this.isQuickMode) {
			if (isEmpty(this._data)) {
				withBasicQueriesError("$data is required");
			}

			const requiredData = ["searchText", "currentPage", "orderBy", "detailId"];
			const missingData = requiredData.filter(dataName => !Object.keys(this._data).includes(dataName));
			if (!isEmpty(missingData)) {
				withBasicQueriesError(`${missingData.length > 1 ? "Keys are" : "Key is"} not declare in $data: ` + missingData.join(", "));
			}

			this.syncQuery([
				{
					dataName: "detailId",
					queryName: "id",
					dataToQuery: {
						immediate: true
					},
					queryToData: {
						formatter: value => Number(value)
					}
				},
				{
					dataName: "searchText",
					queryName: "search",
					dataToQuery: {
						debounceTime: 400
					}
				},
				{
					dataName: "currentPage",
					queryName: "page",
					dataToQuery: {
						formatter: value => (value ? value.toString() : "1"),
						immediate: true
					},
					queryToData: {
						formatter: value => (value ? Number(value) : 1)
					}
				},
				{
					dataName: "orderBy",
					queryName: "sort",
					dataToQuery: {
						formatter: value => (!isEmpty(value) ? `${value.column}-${value.direction}` : null),
						deep: true
					},
					queryToData: {
						formatter: value => {
							if (!isEmpty(value)) {
								const parsedQueryOrderBy = value.split("-");
								return { column: parsedQueryOrderBy[0], direction: parsedQueryOrderBy[1] };
							}
							return null;
						}
					}
				}
			]);
		}
	},
	methods: {
		mixinHandleSearch() {
			clearTimeout(this.timer);
			this.timer = setTimeout(() => {
				this.currentPage = 1;
			}, 400);
		},
		mixinHandleSort(columnName, order) {
			console.log("mixinHandleSort");
			if (columnName) {
				if (order === null) {
					this.orderBy = null;
				} else {
					this.orderBy = { column: columnName, direction: order };
				}
			}
		}
	}
};

export * from "./isMobileMixin";
export * from "./showConfirmMixin";
export * from "./detectLeavePageMixin";

export default {
	computed: {
		userSettings() {
			return this.$store && this.$store.state.auth ? this.$store.state.auth.user_settings : {};
		},
		userTimeFormat() {
			const userSettings = this.userSettings || {};
			return userSettings.time_format || "HH:mm:ss";
		},
		userDateFormat() {
			const userSettings = this.userSettings || {};
			return userSettings.date_format || "DD/MM/YYYY";
		},
		userDateTimeFormat() {
			const userSettings = this.userSettings || {};
			return userSettings.datetime_format || "HH:mm:ss DD/MM/YYYY";
		}
	},
	methods: {
		mixinIsAcceptLeaveConfirm() {
			return new Promise(resolve =>
				this.$vs.dialog({
					type: "confirm",
					color: "danger",
					accept: () => resolve(true),
					cancel: () => resolve(false),
					title: this.$t("Actions.Confirm"),
					text: this.$t("Notify.LeaveConfirm"),
					acceptText: this.$t("Actions.Agree"),
					cancelText: this.$t("Actions.Cancel")
				})
			);
		},
		async handleCloseDialog(setDialogHide, isFormDirty) {
			if (isFormDirty) {
				const isAccept = await this.mixinIsAcceptLeaveConfirm();
				if (isAccept) {
					setDialogHide(true);
				}
			} else {
				setDialogHide(true);
			}
		},
		fileIsImage(extension) {
			return ["jpg", "jpeg", "ico", "png"].includes(extension);
		},
		handleDownloadFile(file) {
			return new Promise((resolve, reject) => {
				const req = new XMLHttpRequest();
				req.open("GET", file.path, true);
				req.responseType = "arraybuffer";

				req.onload = function() {
					const resp = req.response;
					if (resp) {
						const blob = new Blob([resp]);
						const link = document.createElement("a");
						link.href = window.URL.createObjectURL(blob);
						link.download = file.file_name;
						link.click();
						resolve(resp);
					}
				};

				req.onerror = function() {
					reject(req.statusText);
				};

				req.send();
			});
		},
		renderStatusColor(key) {
			return this.$constant.TEXT_STATUS_COLOR[key];
		},
		renderBackgroundStatusColor(key) {
			return this.$constant.BG_STATUS_COLOR[key];
		}
	}
};
